import { useNavigate } from "react-router-dom";
import { formatDate, formatDateAndTime } from "../../../../common/helpers/date.helpers";
import { IPartner, PARTNER_STATUS, PartnerType } from "../../../../types/partners.type";
import { ViewType } from "./view-type";
import { CHARGE_STATUS, ICharge } from "../../../../types/charge.type";

const STATUS_CHARGE = {
  [CHARGE_STATUS.RECEIVED]: "Paga",
  [CHARGE_STATUS.PENDING]: "Pendente",
  [CHARGE_STATUS.OVERDUE]: "Vencida",
};

const STATUS_CHARGE_COLORS = {
  [CHARGE_STATUS.RECEIVED]: "font-semibold text-green-500",
  [CHARGE_STATUS.PENDING]: "font-semibold text-yellow-500",
  [CHARGE_STATUS.OVERDUE]: "font-semibold text-red-500",
};

export function getChargeStatusLabel(status: CHARGE_STATUS): React.ReactNode {
  return (
    <span
      className={`${STATUS_CHARGE_COLORS[status]}`}
      title={STATUS_CHARGE[status] || status}>
      {STATUS_CHARGE[status] || status}
    </span>
  );
}

const STATUS_LABELS = {
  [PARTNER_STATUS.ACTIVE]: "Ativo",
  [PARTNER_STATUS.INACTIVE]: "Inativo",
  [PARTNER_STATUS.DEFAULTER]: "Inadimplente",
}

const STATUS_PARTNER_COLORS = {
  [PARTNER_STATUS.ACTIVE]: "font-semibold text-green-500",
  [PARTNER_STATUS.INACTIVE]: "font-semibold text-gray-500",
  [PARTNER_STATUS.DEFAULTER]: "font-semibold text-red-500",
}

function getStatusLabels(status: PARTNER_STATUS) {
  return (
    <span
      className={`${STATUS_PARTNER_COLORS[status]}`}
      title={STATUS_LABELS[status] || status}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export function getHeadersByViewType(viewType: ViewType): Record<string, string> {
  switch (viewType) {
    case ViewType.PARTNERS:
      return {
        companyName: 'Nome da Empresa',
        ownerName: 'Proprietário',
        status: 'Status',
        updatedAt: 'Atualizado em',
        createdAt: 'Criado em',
      };
    case ViewType.DISPUTES:
      return {
        partName: 'Parceiro',
        notes: 'Fatura',
        date: 'Data da Contestação',
        message: "Descrição da Contestação",
        status: 'Situação',
        messageResponse: "Resposta da Contestação",
      };
    case ViewType.INVOICES:
      return {
        partName: 'Parceiro',
        notes: 'Fatura',
        dueDate: 'Vencimento',
        status: 'Status',
      };
    default:
      return {};
  }
}

export function getViewTitle(viewType: ViewType): string {
  switch (viewType) {
    case ViewType.PARTNERS:
      return "Parceiros";
    case ViewType.DISPUTES:
      return "Contestações";
    case ViewType.INVOICES:
      return "Faturas";
    default:
      return "Parceiros";
  }
}

export function getPlaceholderByViewType(viewType: ViewType): string {
  switch (viewType) {
    case ViewType.PARTNERS:
      return "Digite o nome, o e-mail ou o CNPJ.";
    case ViewType.DISPUTES:
      return "Digite o número da fatura.";
    case ViewType.INVOICES:
      return "Digite o número da fatura.";
    default:
      return "Digite para pesquisar...";
  }
}

export function getStatusOptionsByViewType(viewType: ViewType): Record<string, string> {
  switch (viewType) {
    case ViewType.PARTNERS:
      return {
        [PARTNER_STATUS.ACTIVE]: "Ativo",
        [PARTNER_STATUS.INACTIVE]: "Inativo",
        [PARTNER_STATUS.DEFAULTER]: "Inadimplente",
      };
    case ViewType.DISPUTES:
      return {
        [CHARGE_STATUS.PENDING]: "Pendente",
        [CHARGE_STATUS.RECEIVED]: "Paga",
        [CHARGE_STATUS.OVERDUE]: "Vencida",
      };
    case ViewType.INVOICES:
      return {
        [CHARGE_STATUS.PENDING]: "Pendente",
        [CHARGE_STATUS.RECEIVED]: "Paga",
        [CHARGE_STATUS.OVERDUE]: "Vencida",
      };
    default:
      return {};
  }
}

export function getEntityByViewType(viewType: ViewType): string {
  switch (viewType) {
    case ViewType.PARTNERS:
      return "partners";
    case ViewType.DISPUTES:
      return "partners";
    case ViewType.INVOICES:
      return "partners";
    default:
      return "partners";
  }
}

export async function loadData<T>({
  apiCall,
  setStateFn,
  dataTransformer = (data) => data,
  filters,
  page,
  setData,
  data,
  setIsLoading,
  notifyError
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apiCall: (params: any) => Promise<any>;
  setStateFn: (data: T[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataTransformer?: (data: any[]) => T[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: any;
  page: number;
  setData: (data: { itemsPerPage: number; totalItems: number }) => void;
  data: { itemsPerPage: number; totalItems: number };
  setIsLoading: (loading: boolean) => void;
  notifyError: (message: string) => void;
}) {
  setIsLoading(true);
  setStateFn([] as unknown as T[]);

  try {
    const result = await apiCall({ ...filters, page });
    setData({ itemsPerPage: data.itemsPerPage, totalItems: result.totalItems });

    if (!result.ok)
      return notifyError(result.message || "unknown error");

    const transformedData = dataTransformer(result.data);

    setData({ itemsPerPage: data.itemsPerPage, totalItems: result.totalItems });
    setStateFn(transformedData as T[]);
  } catch (error) {
    console.error("Error loading data:", error);
    notifyError("Erro ao carregar dados");
  } finally {
    setIsLoading(false);
  }
}

export function getActionsByViewType(
  viewType: ViewType,
  setAction: (action: { eventName: string, data?: unknown }) => void,
  notifyError: (msg: string) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Array<{ label: string; action: (data: any) => void; permission?: string }> | undefined {
  switch (viewType) {
    case ViewType.PARTNERS:
      return [
        {
          label: "Editar",
          action: (data) => setAction({ eventName: 'edit', data: (data as PartnerType)._id }),
          permission: "edit:partners"
        }
      ];

    case ViewType.INVOICES:
      return [
        {
          label: "Visualizar Fatura",
          action: (data) => setAction({ eventName: 'viewInvoice', data: (data as ICharge).externalId }),
          permission: "view:partners"
        }
      ];

    case ViewType.DISPUTES:
      return [
        {
          label: "Responder Contestação",
          action: (data) => {
            if (data._original.contest?.status !== "closed") {
              setAction({ eventName: 'changeStatus', data: data._original as unknown });
            } else {
              notifyError("Essa fatura já foi contestada.");
            }
          },
          permission: "edit:partners"
        }
      ];

    default:
      return undefined;
  }
}

export function getRendersByViewType(
  viewType: ViewType,
  navigate: ReturnType<typeof useNavigate>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, (value: any, rowData?: any) => React.ReactNode> {
  switch (viewType) {
    case ViewType.PARTNERS:
      return {
        ownerName: (ownerName, partners) => (
          <a
            href={`/parceiros/${(partners as IPartner)._id}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/parceiros/${(partners as IPartner)._id}`);
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {ownerName as string}
          </a>
        ),
        updatedAt: formatDateAndTime,
        createdAt: formatDateAndTime,
        status: getStatusLabels,
      };

    default:
      return {};
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function formatDisputeOrInvoice(viewType: ViewType, item: any) {
  if (viewType === ViewType.DISPUTES) {
    return {
      status: item.contest?.status
        ? item.contest.status === "open"
          ? "Aberto"
          : "Fechado"
        : "-",
      partName: item?.partneName || "",
      notes: item?.notes || "",
      message: item?.contest?.message,
      date: item?.contest?.date ? formatDate(item.contest.date) : "",
      messageResponse: item?.contest?.messageResponse || "-",
      _original: item,
    };
  }
  if (viewType === ViewType.INVOICES) {
    return {
      partName: item.partneName,
      status: getChargeStatusLabel(item.status),
      notes: item.notes,
      dueDate: item.dueDate ? formatDate(item.dueDate) : "",
      externalId: item.externalId,
      _original: item,
    };
  }
  // fallback para partners ou outros
  return item;
}